import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Database, Download, Pencil, ScrollText, ShieldCheck, Upload, UserCog } from 'lucide-react';
import { APP_CONFIG } from '../../core/config';
import { canSeeRoleBadge, hasPermission, ROLE_LABELS } from '../../core/permissions';
import { formatPhone } from '../../core/phone';
import { DB_NAME, DB_VERSION } from '../../db/index';
import { listAuditLogs } from '../../db/audit-store';
import { listUsers, preferencesOf } from '../../db/auth-store';
import {
  BACKUP_TABLES,
  EXCLUDED_TABLES,
  backupFileName,
  databaseCounts,
  exportDatabase,
  resetToSeedData,
  restoreFromText,
  serializeBackup,
} from '../../db/backup-store';
import { Modal } from '../../components/ui/Modal';
import { fieldClass } from '../../components/ui/form-classes';
import { AUDIT_ACTIONS, AUDIT_ACTION_LABELS } from '../../types/audit';
import type { AuditAction, AuditLogEntry } from '../../types/audit';
import type { User } from '../../types/user';
import { AuditLogTable } from './AuditLogTable';
import { ProfileModal } from './ProfileModal';
import { UserAccessModal } from './UserAccessModal';

interface SettingsPageProps {
  user: User;
  /** Re-reads the board and the fleet after a restore or a reset. */
  onDataChanged: () => Promise<void>;
  /** Hands the edited account back to the shell, so the header follows it. */
  onProfileUpdated?: (user: User) => void;
}

type Notice = { tone: 'ok' | 'error'; text: string } | null;

/** Hands the serialised backup to the browser as a downloaded file. */
function downloadJsonFile(fileName: string, text: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: 'application/json' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/**
 * System settings: the operator's own details, the audit ledger, a full JSON
 * backup of the local database, a restore from one, and the emergency reset
 * back to a clean slate.
 *
 * Every data action here is guarded twice — once on this screen, so an
 * operation that cannot complete is never offered, and again inside
 * `src/db/backup-store.ts`, which is what actually protects the data. The
 * profile section is the exception on purpose: an account always owns its own
 * name, phone and display choices.
 */
export function SettingsPage({ user, onDataChanged, onProfileUpdated }: SettingsPageProps) {
  const [editingProfile, setEditingProfile] = useState(false);
  const [managingUsers, setManagingUsers] = useState(false);
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [actorNames, setActorNames] = useState<Record<string, string>>({});
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [action, setAction] = useState<AuditAction | 'all'>('ASSIGNMENT_OVERRIDE');
  const [notice, setNotice] = useState<Notice>(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState<'reset' | null>(null);
  const [pendingRestore, setPendingRestore] = useState<{ name: string; text: string } | null>(null);

  const canManageUsers = hasPermission(user, 'MANAGE_USERS');
  const canViewAudit = hasPermission(user, 'VIEW_AUDIT_LOGS');
  const canExport = hasPermission(user, 'EXPORT_DATA');
  const canManageSystem = hasPermission(user, 'MANAGE_SYSTEM_SETTINGS');

  const load = useCallback(async () => {
    const [entries, users, tableCounts] = await Promise.all([
      canViewAudit ? listAuditLogs() : Promise.resolve([]),
      listUsers(),
      databaseCounts(),
    ]);
    setLogs(entries);
    setActorNames(Object.fromEntries(users.map((entry) => [entry.id, entry.fullName])));
    setCounts(tableCounts);
  }, [canViewAudit]);

  useEffect(() => {
    void load();
  }, [load]);

  const visibleLogs = useMemo(
    () => (action === 'all' ? logs : logs.filter((entry) => entry.action === action)),
    [logs, action],
  );

  const totalRows = Object.values(counts).reduce((sum, count) => sum + count, 0);

  const handleExport = async () => {
    setBusy(true);
    setNotice(null);
    try {
      const backup = await exportDatabase(user);
      const fileName = backupFileName();
      downloadJsonFile(fileName, serializeBackup(backup));
      setNotice({ tone: 'ok', text: `הגיבוי נשמר בקובץ ${fileName} — ${Object.values(backup.counts).reduce((sum, count) => sum + count, 0)} רשומות.` });
    } catch (error) {
      setNotice({ tone: 'error', text: (error as Error).message });
    } finally {
      setBusy(false);
    }
  };

  const handleFilePicked = async (file: File | null | undefined) => {
    if (!file) return;
    setNotice(null);
    try {
      setPendingRestore({ name: file.name, text: await file.text() });
    } catch (error) {
      setNotice({ tone: 'error', text: (error as Error).message });
    }
  };

  const handleRestore = async () => {
    if (!pendingRestore) return;
    setBusy(true);
    setNotice(null);
    try {
      const summary = await restoreFromText(user, pendingRestore.text);
      await onDataChanged();
      await load();
      setNotice({
        tone: 'ok',
        text: `השחזור הושלם — ${summary.total} רשומות נטענו מתוך ${pendingRestore.name}.${
          summary.skipped.length > 0 ? ` טבלאות שלא זוהו: ${summary.skipped.join(', ')}.` : ''
        }`,
      });
      setPendingRestore(null);
    } catch (error) {
      setNotice({ tone: 'error', text: (error as Error).message });
    } finally {
      setBusy(false);
    }
  };

  const handleReset = async () => {
    setBusy(true);
    setNotice(null);
    try {
      await resetToSeedData(user);
      await onDataChanged();
      await load();
      setNotice({ tone: 'ok', text: 'מסד הנתונים אופס ונזרע מחדש בנתוני ברירת המחדל.' });
      setConfirming(null);
    } catch (error) {
      setNotice({ tone: 'error', text: (error as Error).message });
    } finally {
      setBusy(false);
    }
  };

  const preferences = preferencesOf(user);

  return (
    <div className="mx-auto w-full max-w-5xl space-y-4">
      <section className="bg-panel border border-line rounded-2xl p-5">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-semibold flex items-center gap-2">
            <UserCog size={18} className="text-brand-500" aria-hidden />
            הפרטים האישיים שלי
          </h3>
          <button
            type="button"
            onClick={() => setEditingProfile(true)}
            className="h-9 px-3 rounded-xl border border-line text-sm text-ink/70 hover:text-ink flex items-center gap-1.5"
          >
            <Pencil size={15} aria-hidden />
            עריכת פרטים אישיים
          </button>
        </div>
        <dl className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
          <div className="rounded-xl border border-line p-3">
            <dt className="text-xs text-ink/50">שם מלא</dt>
            <dd>{user.fullName}</dd>
          </div>
          <div className="rounded-xl border border-line p-3">
            <dt className="text-xs text-ink/50">טלפון</dt>
            <dd data-dir="ltr">{formatPhone(user.phone)}</dd>
          </div>
          <div className="rounded-xl border border-line p-3">
            <dt className="text-xs text-ink/50">מספר אישי</dt>
            <dd data-dir="ltr">{user.personalNumber}</dd>
          </div>
          <div className="rounded-xl border border-line p-3">
            <dt className="text-xs text-ink/50">תג בכותרת</dt>
            <dd>
              {canSeeRoleBadge(user)
                ? preferences.showRoleBadge
                  ? ROLE_LABELS[user.role]
                  : 'מוסתר'
                : 'ללא תג תפקיד'}
            </dd>
          </div>
        </dl>
      </section>

      <section className="bg-panel border border-line rounded-2xl p-5">
        <h2 className="text-xl font-bold">הגדרות מערכת ונתונים</h2>
        <p className="text-sm text-ink/60 mt-1">
          יומן ביקורת, גיבוי מלא של הנתונים המקומיים, שחזור ואיפוס חירום.
        </p>
        <dl className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
          <div className="rounded-xl border border-line p-3">
            <dt className="text-xs text-ink/50">מזהה מערכת</dt>
            <dd data-dir="ltr">{APP_CONFIG.appId}</dd>
          </div>
          <div className="rounded-xl border border-line p-3">
            <dt className="text-xs text-ink/50">מסד נתונים מקומי</dt>
            <dd data-dir="ltr">
              {DB_NAME} · v{DB_VERSION}
            </dd>
          </div>
          <div className="rounded-xl border border-line p-3">
            <dt className="text-xs text-ink/50">רשומות שמורות</dt>
            <dd className="tabular-nums">{totalRows}</dd>
          </div>
          <div className="rounded-xl border border-line p-3">
            <dt className="text-xs text-ink/50">גרסת אפליקציה</dt>
            <dd data-dir="ltr">v{APP_CONFIG.version}</dd>
          </div>
        </dl>
      </section>

      {notice ? (
        <p
          className={[
            'rounded-2xl border p-3 text-sm',
            notice.tone === 'ok'
              ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
              : 'border-rose-300 bg-rose-50 text-rose-700',
          ].join(' ')}
          role="status"
        >
          {notice.text}
        </p>
      ) : null}

      {/* The directory is the מנהל על's alone — `MANAGE_USERS` is
          super-admin-only, so a commander never sees this section at all. */}
      {canManageUsers ? (
        <section className="bg-panel border border-line rounded-2xl p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="font-semibold flex items-center gap-2">
                <ShieldCheck size={18} className="text-brand-500" aria-hidden />
                ניהול הרשאות ומשתמשים
              </h3>
              <p className="text-sm text-ink/60 mt-1">
                פתיחת חשבונות לחברי הצוות, קביעת רמת ההרשאה, הפעלה או השבתה של גישה ואיפוס קוד
                הכניסה בן 4 הספרות.
              </p>
            </div>
            <button
              type="button"
              data-open-user-management
              onClick={() => setManagingUsers(true)}
              className="shrink-0 h-9 px-3 rounded-xl bg-brand-500 text-white text-sm font-medium hover:bg-brand-700"
            >
              פתיחת הניהול
            </button>
          </div>
        </section>
      ) : null}

      <section className="bg-panel border border-line rounded-2xl p-5">
        <h3 className="font-semibold flex items-center gap-2">
          <ScrollText size={18} className="text-brand-500" aria-hidden />
          יומן ביקורת — עקיפות מנהל
        </h3>
        {!canViewAudit ? (
          <p className="text-sm text-ink/60 mt-2">
            נדרשת הרשאת "צפייה ביומן הביקורת" כדי לקרוא את רשומות העקיפה.
          </p>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3 mt-3 mb-3">
              <label className="text-sm">
                <span className="text-xs text-ink/50 me-2">סוג פעולה</span>
                <select
                  value={action}
                  onChange={(event) => setAction(event.target.value as AuditAction | 'all')}
                  className={`${fieldClass} w-auto inline-block`}
                >
                  <option value="ASSIGNMENT_OVERRIDE">
                    {AUDIT_ACTION_LABELS.ASSIGNMENT_OVERRIDE}
                  </option>
                  <option value="all">כל הפעולות</option>
                  {AUDIT_ACTIONS.filter((entry) => entry !== 'ASSIGNMENT_OVERRIDE').map((entry) => (
                    <option key={entry} value={entry}>
                      {AUDIT_ACTION_LABELS[entry]}
                    </option>
                  ))}
                </select>
              </label>
              <p className="text-xs text-ink/50">
                {visibleLogs.length} רשומות · היומן הוא append-only ואינו ניתן לעריכה
              </p>
            </div>
            <AuditLogTable entries={visibleLogs} actorNames={actorNames} />
          </>
        )}
      </section>

      <section className="bg-panel border border-line rounded-2xl p-5">
        <h3 className="font-semibold flex items-center gap-2">
          <Database size={18} className="text-brand-500" aria-hidden />
          גיבוי, ייצוא ושחזור נתונים
        </h3>
        <p className="text-sm text-ink/60 mt-1">
          הגיבוי כולל {BACKUP_TABLES.length} טבלאות. קודי הכניסה המקומיים והסשן הפעיל
          ({EXCLUDED_TABLES.join(', ')}) אינם נכללים בקובץ — הם שייכים למכשיר ולא לנתונים.
        </p>

        <div className="grid sm:grid-cols-2 gap-3 mt-4">
          <div className="rounded-xl border border-line p-4">
            <p className="font-medium text-sm mb-1">ייצוא גיבוי</p>
            <p className="text-xs text-ink/50 mb-3">קובץ JSON יחיד עם חותמת תאריך.</p>
            <button
              type="button"
              disabled={!canExport || busy}
              onClick={handleExport}
              className="h-10 px-4 rounded-xl bg-brand-500 text-white text-sm font-medium hover:bg-brand-700 disabled:opacity-40 flex items-center gap-2"
            >
              <Download size={16} aria-hidden />
              ייצוא מסד הנתונים
            </button>
            {!canExport ? (
              <p className="text-xs text-ink/50 mt-2">נדרשת הרשאת "ייצוא נתונים".</p>
            ) : null}
          </div>

          <div className="rounded-xl border border-line p-4">
            <p className="font-medium text-sm mb-1">שחזור מגיבוי</p>
            <p className="text-xs text-ink/50 mb-3">
              הקובץ מחליף את כל הנתונים הקיימים במכשיר זה.
            </p>
            <label
              className={[
                'h-10 px-4 rounded-xl border border-line text-sm font-medium inline-flex items-center gap-2',
                canManageSystem && !busy ? 'cursor-pointer hover:border-brand-300' : 'opacity-40',
              ].join(' ')}
            >
              <Upload size={16} aria-hidden />
              בחירת קובץ גיבוי
              <input
                type="file"
                accept="application/json,.json"
                className="hidden"
                disabled={!canManageSystem || busy}
                onChange={(event) => {
                  void handleFilePicked(event.target.files?.[0]);
                  event.target.value = '';
                }}
              />
            </label>
            {!canManageSystem ? (
              <p className="text-xs text-ink/50 mt-2">שחזור שמור למנהל על בלבד.</p>
            ) : null}
          </div>
        </div>

        <details className="mt-4">
          <summary className="text-sm text-ink/60 cursor-pointer">פירוט הרשומות לפי טבלה</summary>
          <dl className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            {BACKUP_TABLES.map((table) => (
              <div key={table} className="rounded-lg border border-line p-2 flex justify-between">
                <dt className="text-ink/50" data-dir="ltr">
                  {table}
                </dt>
                <dd className="tabular-nums font-semibold">{counts[table] ?? 0}</dd>
              </div>
            ))}
          </dl>
        </details>
      </section>

      <section className="bg-panel border border-rose-300 rounded-2xl p-5">
        <h3 className="font-semibold flex items-center gap-2 text-rose-700">
          <AlertTriangle size={18} aria-hidden />
          איפוס חירום
        </h3>
        <p className="text-sm text-ink/60 mt-1">
          מוחק את כל המשימות, השיבוצים, הצי והיומן במכשיר זה ומחזיר את המערכת ללוח ריק עם קטלוג
          סוגי המשימות בלבד. החשבונות והמכשירים המאושרים נשמרים.
        </p>
        <button
          type="button"
          disabled={!canManageSystem || busy}
          onClick={() => setConfirming('reset')}
          className="mt-3 h-10 px-4 rounded-xl border border-rose-300 bg-rose-50 text-rose-700 text-sm font-medium hover:bg-rose-100 disabled:opacity-40"
        >
          איפוס מסד הנתונים ללוח ריק
        </button>
        {!canManageSystem ? (
          <p className="text-xs text-ink/50 mt-2">האיפוס שמור למנהל על בלבד.</p>
        ) : null}
      </section>

      {pendingRestore ? (
        <Modal
          title="שחזור מגיבוי"
          subtitle={pendingRestore.name}
          onClose={() => setPendingRestore(null)}
          footer={
            <>
              <button
                type="button"
                onClick={() => setPendingRestore(null)}
                className="h-10 px-4 rounded-xl border border-line text-sm"
              >
                ביטול
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={handleRestore}
                className="h-10 px-4 rounded-xl bg-brand-500 text-white text-sm font-medium hover:bg-brand-700 disabled:opacity-40"
              >
                שחזר והחלף את הנתונים
              </button>
            </>
          }
        >
          <p className="text-sm">
            כל הנתונים התפעוליים במכשיר זה יוחלפו בתוכן הקובץ. הפעולה אינה הפיכה — מומלץ לייצא
            גיבוי לפני השחזור.
          </p>
        </Modal>
      ) : null}

      {confirming === 'reset' ? (
        <Modal
          title="איפוס מסד הנתונים"
          onClose={() => setConfirming(null)}
          footer={
            <>
              <button
                type="button"
                onClick={() => setConfirming(null)}
                className="h-10 px-4 rounded-xl border border-line text-sm"
              >
                ביטול
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={handleReset}
                className="h-10 px-4 rounded-xl bg-rose-600 text-white text-sm font-medium hover:bg-rose-700 disabled:opacity-40"
              >
                איפוס מלא
              </button>
            </>
          }
        >
          <p className="text-sm">
            כל המשימות, הצוותים, סל המחזור, יומן הביקורת וציר הזמן יימחקו מהמכשיר, והמערכת תחזור
            ללוח ריק. אין דרך לשחזר את הנתונים ללא קובץ גיבוי.
          </p>
        </Modal>
      ) : null}

      {managingUsers ? (
        <UserAccessModal
          user={user}
          onClose={() => setManagingUsers(false)}
          // A directory change files ledger rows; the log on this screen
          // re-reads so the action the operator just took is visible.
          onChanged={() => void load()}
        />
      ) : null}

      {editingProfile ? (
        <ProfileModal
          user={user}
          onClose={() => setEditingProfile(false)}
          onSaved={(updated) => onProfileUpdated?.(updated)}
        />
      ) : null}
    </div>
  );
}
